import { recommendationsForTier } from '../data/modelRegistry'
import type {
  Assessment,
  ModelTier,
  PromptSignals,
  SignalReason,
  TaskType,
  WorkItem,
} from '../domain/types'

const clamp = (value: number, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Math.round(value)))

const PATTERNS: Array<{
  type: TaskType
  pattern: RegExp
}> = [
  {
    type: 'extract',
    pattern:
      /\b(extract|parse|pull out|return fields?|convert|transform|format)\b/i,
  },
  {
    type: 'classify',
    pattern:
      /\b(classif(?:y|ication)|categorize|label|route|score)\b/i,
  },
  {
    type: 'rewrite',
    pattern:
      /\b(rewrite|edit|proofread|translate|summarize|shorten)\b/i,
  },
  {
    type: 'generate',
    pattern:
      /\b(generate|draft|write|create copy|brainstorm)\b/i,
  },
  {
    type: 'implement',
    pattern:
      /\b(implement|build|add|change|fix|refactor|migrate|deploy)\b/i,
  },
  {
    type: 'debug',
    pattern:
      /\b(debug|diagnos(?:e|is)|root cause|intermittent|investigate|why .*fail)\b/i,
  },
  {
    type: 'review',
    pattern:
      /\b(review|audit|critique|check|verify|evaluate)\b/i,
  },
  {
    type: 'research',
    pattern:
      /\b(research|look up|find sources?|benchmark|compare providers?|current|latest)\b/i,
  },
  {
    type: 'plan',
    pattern:
      /\b(plan|design|architect|strategy|roadmap|decompose|trade-?offs?)\b/i,
  },
]

const BROAD_TERMS =
  /\b(improve|make (?:it|this) better|clean up|figure (?:it|this) out|anything related|as needed|best practices?|etc\.?|somehow|nice|good)\b/gi
const CONSTRAINT_TERMS =
  /\b(must|must not|do not|only|without|keep|preserve|limit|at most|exactly|no more than|avoid|require)\b/gi
const SUCCESS_TERMS =
  /\b(success (?:means|criteria)|acceptance criteria|done when|passes?|tests?|verify|validation|expected|should return|output schema|definition of done)\b/gi
const OUTPUT_TERMS =
  /\b(json|yaml|csv|table|list|bullets?|report|memo|diff|patch|file|html|markdown|schema|format)\b/gi
const TOOL_TERMS =
  /\b(tool|shell|terminal|browser|web search|api|database|repository|repo|git|github|deploy|worker|test suite|files?)\b/gi
const ARTIFACT_TERMS =
  /\b(files?|components?|services?|systems?|providers?|models?|documents?|sources?|repositories?|tables?|endpoints?|workers?|databases?)\b/gi
const DOMAIN_TERMS =
  /\b(security|cryptograph|distributed|concurren|legal|medical|financial|tax|compliance|infrastructure|database migration|authentication|authorization)\b/gi
const RISK_TERMS =
  /\b(delete|drop|destroy|overwrite|production|deploy|release|credential|secret|payment|legal|medical|security|migration|customer data|personal data)\b/gi
const ROOT_CAUSE_TERMS =
  /\b(root cause|intermittent|race condition|non-?deterministic|memory leak|deadlock|across .* systems?|only .* after|reproduce|regression)\b/gi
const CURRENT_TERMS =
  /\b(current|latest|today|recent|news|price|pricing|schedule|version|release|official sources?|citations?)\b/gi
const LONG_CONTEXT_TERMS =
  /\b(entire repo|whole repo|large codebase|all files|long documents?|source corpus|many documents?|transcripts?|cross-repository)\b/gi

function countMatches(value: string, pattern: RegExp): number {
  return Array.from(value.matchAll(pattern)).length
}

function uniqueTaskTypes(prompt: string): TaskType[] {
  const types = PATTERNS.filter(({ pattern }) =>
    pattern.test(prompt),
  ).map(({ type }) => type)
  return types.length > 0 ? Array.from(new Set(types)) : ['other']
}

function countEnumeratedItems(prompt: string): number {
  return prompt
    .split('\n')
    .filter((line) => /^\s*(?:[-*]|\d+[.)])\s+\S/.test(line))
    .length
}

function hasNamedContext(prompt: string): boolean {
  return (
    /(?:^|[\s`])(?:\.{0,2}\/)?[\w-]+\/[\w./-]+\.[a-z0-9]+(?:$|[\s`,:])/i.test(
      prompt,
    ) ||
    /\b(?:function|class|component|endpoint|table|repository|repo|file|URL|https?:\/\/)\s+[`"']?[\w./:#-]+/i.test(
      prompt,
    )
  )
}

function extractSignals(workItem: WorkItem): PromptSignals {
  const prompt = workItem.prompt.trim()
  const taskTypes = uniqueTaskTypes(prompt)
  const enumeratedItems = countEnumeratedItems(prompt)
  const conjunctions = countMatches(
    prompt,
    /\b(and then|then|after that|before|after|also|finally|as well as)\b/gi,
  )
  const artifactCount = countMatches(prompt, ARTIFACT_TERMS)
  const toolCount = countMatches(prompt, TOOL_TERMS)
  const broadCount = countMatches(prompt, BROAD_TERMS)
  const constraintCount = countMatches(prompt, CONSTRAINT_TERMS)
  const successCount = countMatches(prompt, SUCCESS_TERMS)
  const outputCount = countMatches(prompt, OUTPUT_TERMS)
  const domainCount = countMatches(prompt, DOMAIN_TERMS)
  const riskCount = countMatches(prompt, RISK_TERMS)
  const rootCauseCount = countMatches(prompt, ROOT_CAUSE_TERMS)
  const currentCount = countMatches(prompt, CURRENT_TERMS)
  const longContextCount = countMatches(prompt, LONG_CONTEXT_TERMS)
  const namedContext = hasNamedContext(prompt)
  const hasAction = taskTypes.some((type) => type !== 'other')
  const wordCount = prompt.split(/\s+/).filter(Boolean).length

  const estimatedSteps = clamp(
    Math.max(
      1,
      enumeratedItems,
      taskTypes.length + Math.ceil(conjunctions / 2),
    ),
    1,
    12,
  )

  const goalClarity = clamp(
    35 +
      (hasAction ? 30 : 0) +
      (wordCount >= 8 ? 15 : 0) +
      (outputCount > 0 ? 10 : 0) -
      broadCount * 8,
  )
  const constraintClarity = clamp(
    22 +
      Math.min(48, constraintCount * 12) +
      (enumeratedItems > 1 ? 10 : 0) +
      (workItem.targetQuality === 'production' ? 10 : 0),
  )
  const acceptanceCriteria = clamp(
    15 +
      Math.min(60, successCount * 18) +
      (outputCount > 1 ? 10 : 0),
  )
  const contextCompleteness = clamp(
    20 +
      (namedContext ? 35 : 0) +
      Math.min(25, toolCount * 6) +
      (workItem.contextDescription ? 15 : 0),
  )
  const outputSpecificity = clamp(
    20 +
      Math.min(55, outputCount * 15) +
      (successCount > 0 ? 10 : 0),
  )

  return {
    taskTypes,
    estimatedSteps,
    ambiguity: clamp(
      20 +
        broadCount * 17 +
        (wordCount < 8 ? 25 : 0) -
        constraintCount * 7 -
        successCount * 8,
    ),
    scopeBreadth: clamp(
      12 +
        artifactCount * 8 +
        Math.max(0, taskTypes.length - 1) * 10 +
        Math.min(20, conjunctions * 4),
    ),
    crossArtifactReasoning: clamp(
      8 +
        Math.max(0, artifactCount - 1) * 14 +
        (/\bacross\b/i.test(prompt) ? 18 : 0),
    ),
    rootCauseDepth: clamp(
      rootCauseCount * 30 +
        (taskTypes.includes('debug') ? 22 : 0),
    ),
    domainExpertise: clamp(
      domainCount * 20 +
        (/\bspecialist|expert|domain\b/i.test(prompt) ? 15 : 0),
    ),
    verificationBurden: clamp(
      successCount * 16 +
        (workItem.targetQuality === 'production' ? 28 : 0) +
        (taskTypes.includes('review') ? 12 : 0),
    ),
    toolDependence: clamp(
      toolCount * 14 +
        (workItem.availableTools.length > 0
          ? Math.min(25, workItem.availableTools.length * 5)
          : 0) +
        currentCount * 8,
    ),
    longContextNeed: clamp(
      longContextCount * 30 +
        (wordCount > 1200 ? 40 : wordCount > 500 ? 20 : 0),
    ),
    stakes: clamp(
      riskCount * 12 +
        (workItem.stakes === 'high'
          ? 65
          : workItem.stakes === 'normal'
            ? 28
            : 8),
    ),
    goalClarity,
    constraintClarity,
    acceptanceCriteria,
    contextCompleteness,
    outputSpecificity,
  }
}

function scoreAssessment(signals: PromptSignals) {
  const operationalDepth = clamp(
    ((signals.estimatedSteps - 1) / 9) * 100,
  )
  const reasoningDepth = Math.max(
    ...signals.taskTypes.map((taskType) => {
      const weights: Record<TaskType, number> = {
        extract: 8,
        classify: 8,
        rewrite: 10,
        generate: 22,
        implement: 32,
        debug: 55,
        review: 28,
        research: 48,
        plan: 45,
        other: 18,
      }
      return weights[taskType]
    }),
  )
  const taskDemand = clamp(
    operationalDepth * 0.13 +
      reasoningDepth * 0.16 +
      signals.scopeBreadth * 0.12 +
      signals.crossArtifactReasoning * 0.12 +
      signals.rootCauseDepth * 0.18 +
      signals.domainExpertise * 0.09 +
      signals.verificationBurden * 0.09 +
      signals.toolDependence * 0.06 +
      signals.longContextNeed * 0.05,
  )
  const promptReadiness = clamp(
    signals.goalClarity * 0.24 +
      signals.constraintClarity * 0.2 +
      signals.acceptanceCriteria * 0.22 +
      signals.contextCompleteness * 0.18 +
      signals.outputSpecificity * 0.16,
  )
  const executionRisk = clamp(
    signals.stakes * 0.62 +
      signals.verificationBurden * 0.2 +
      signals.domainExpertise * 0.18,
  )

  return {
    taskDemand,
    promptReadiness,
    executionRisk,
  }
}

function tierForScores(
  taskDemand: number,
  executionRisk: number,
  workItem: WorkItem,
  signals: PromptSignals,
): ModelTier {
  if (
    taskDemand >= 35 ||
    executionRisk >= 60 ||
    signals.rootCauseDepth >= 55 ||
    (workItem.stakes === 'high' && executionRisk >= 45) ||
    (signals.estimatedSteps >= 5 &&
      signals.taskTypes.some((taskType) =>
        ['debug', 'research', 'plan'].includes(taskType),
      ))
  ) {
    return 'deep'
  }
  if (
    taskDemand >= 12 ||
    executionRisk >= 32 ||
    workItem.targetQuality === 'production'
  ) {
    return 'standard'
  }
  return 'direct'
}

function reasonsFor(
  signals: PromptSignals,
  scores: ReturnType<typeof scoreAssessment>,
): SignalReason[] {
  const reasons: SignalReason[] = []
  const taskLabels: Record<TaskType, string> = {
    extract: 'structured extraction',
    classify: 'classification',
    rewrite: 'rewriting',
    generate: 'content generation',
    implement: 'implementation',
    debug: 'debugging',
    review: 'review',
    research: 'research',
    plan: 'planning',
    other: 'general work',
  }

  reasons.push({
    id: 'task-types',
    label: signals.taskTypes
      .map((type) => taskLabels[type])
      .join(' + '),
    detail: 'Task family detected from the requested actions.',
    impact: 'up',
  })

  if (signals.rootCauseDepth >= 40) {
    reasons.push({
      id: 'root-cause',
      label: 'Root-cause reasoning',
      detail:
        'The prompt asks for diagnosis across uncertain causes, not a known edit.',
      impact: 'up',
    })
  }
  if (signals.scopeBreadth >= 45) {
    reasons.push({
      id: 'scope',
      label: 'Broad scope',
      detail:
        'Several artifacts, systems, or deliverables appear to be coupled.',
      impact: 'up',
    })
  }
  if (signals.toolDependence >= 45) {
    reasons.push({
      id: 'tools',
      label: 'Tool-dependent work',
      detail:
        'Completion likely depends on files, tools, retrieval, or external state.',
      impact: 'up',
    })
  }
  if (signals.stakes >= 60) {
    reasons.push({
      id: 'stakes',
      label: 'Material execution risk',
      detail:
        'Production, security, destructive, or high-stakes language raises the review bar.',
      impact: 'risk',
    })
  }
  if (signals.acceptanceCriteria >= 55) {
    reasons.push({
      id: 'acceptance',
      label: 'Useful completion criteria',
      detail:
        'The prompt provides a way to verify whether the work is actually done.',
      impact: 'readiness',
    })
  }
  if (signals.contextCompleteness >= 55) {
    reasons.push({
      id: 'context',
      label: 'Concrete context',
      detail:
        'Named files, systems, tools, or sources reduce executor guesswork.',
      impact: 'readiness',
    })
  }
  if (signals.ambiguity >= 55) {
    reasons.push({
      id: 'ambiguity',
      label: 'Unresolved ambiguity',
      detail:
        'Broad language leaves important requirements for the executor to invent.',
      impact: 'down',
    })
  }
  if (scores.promptReadiness < 45) {
    reasons.push({
      id: 'readiness',
      label: 'Prompt needs a tighter contract',
      detail:
        'A stronger model cannot reliably substitute for missing context or acceptance criteria.',
      impact: 'down',
    })
  }

  return reasons.slice(0, 6)
}

function improvementsFor(
  workItem: WorkItem,
  signals: PromptSignals,
  scores: ReturnType<typeof scoreAssessment>,
): string[] {
  const improvements: string[] = []

  if (signals.goalClarity < 65) {
    improvements.push(
      'Name the concrete user-visible outcome in one sentence.',
    )
  }
  if (signals.contextCompleteness < 55) {
    improvements.push(
      'Identify the relevant files, systems, inputs, or source material.',
    )
  }
  if (signals.acceptanceCriteria < 55) {
    improvements.push(
      'Add acceptance criteria or the exact check that proves completion.',
    )
  }
  if (signals.constraintClarity < 50) {
    improvements.push(
      'State hard constraints, non-goals, and approval boundaries.',
    )
  }
  if (signals.outputSpecificity < 55) {
    improvements.push(
      'Specify the final deliverable and required output shape.',
    )
  }
  if (scores.taskDemand >= 60 && signals.estimatedSteps >= 5) {
    improvements.push(
      'Split discovery, implementation, and verification into separate work packets.',
    )
  }
  if (scores.executionRisk >= 60) {
    improvements.push(
      'Add a rollback, review, or explicit confirmation gate before risky actions.',
    )
  }
  if (
    /\b(current|latest|today|recent|news|price|pricing|schedule|version|release|official sources?|citations?)\b/i.test(
      workItem.prompt,
    )
  ) {
    improvements.push(
      'Require current primary sources and separate sourced facts from inference.',
    )
  }
  if (
    /\b(think step by step|show your reasoning|chain of thought)\b/i.test(
      workItem.prompt,
    )
  ) {
    improvements.push(
      'Ask for the answer, decisive evidence, and checks—not hidden chain-of-thought.',
    )
  }

  return Array.from(new Set(improvements)).slice(0, 4)
}

export function analyzeWorkItem(workItem: WorkItem): Assessment {
  const signals = extractSignals(workItem)
  const scores = scoreAssessment(signals)
  const tier = tierForScores(
    scores.taskDemand,
    scores.executionRisk,
    workItem,
    signals,
  )
  const effort =
    tier === 'deep'
      ? signals.estimatedSteps >= 9
        ? 'xhigh'
        : 'high'
      : tier === 'standard'
        ? 'medium'
        : scores.taskDemand < 18 && scores.executionRisk < 25
          ? 'none'
          : 'low'

  return {
    tier,
    effort,
    ...scores,
    signals,
    reasons: reasonsFor(signals, scores),
    improvements: improvementsFor(workItem, signals, scores),
    providerRecommendations: recommendationsForTier(tier),
    semanticMatches: [],
    methodology: 'rules-only',
    abstained: false,
  }
}

export const DEFAULT_WORK_ITEM: WorkItem = {
  prompt: '',
  executionMode: 'chat',
  availableTools: [],
  stakes: 'normal',
  targetQuality: 'usable',
}

export function withSemanticMatches(
  assessment: Assessment,
  semanticMatches: Assessment['semanticMatches'],
): Assessment {
  return {
    ...assessment,
    semanticMatches,
    methodology: 'hybrid',
  }
}
